import { useMemo, useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Home,
  Loader2,
  MapPin,
  Package,
  Search,
  User,
  UserCheck,
  X,
} from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "~/components/ui/collapsible";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import { useToast } from "~/hooks/use-toast";
import { cn } from "~/lib/utils";
import { pickingService } from "~/services/wms/pickingService";
import { usePickTaskSelection } from "../hooks/usePickTaskSelection";

interface AssignPopoverProps {
  taskId: string;
  currentAssigneeId?: string;
  onAssigned: () => void;
}

function AssignPopover({
  taskId,
  currentAssigneeId,
  onAssigned,
}: AssignPopoverProps) {
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>(
    currentAssigneeId ?? ""
  );
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: bodegaUsers, isLoading: loadingUsers } = useQuery({
    queryKey: ["bodega-users"],
    queryFn: () => pickingService.getAssignableBodegaUsers(),
    staleTime: 60_000,
  });

  const assignMutation = useMutation({
    mutationFn: () => pickingService.assignPickTask(taskId, selectedUserId),
    onSuccess: () => {
      toast({ title: "Tarea asignada correctamente" });
      queryClient.invalidateQueries({ queryKey: ["picking-tasks-assignment"] });
      setOpen(false);
      onAssigned();
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const selectedUser = bodegaUsers?.find((u) => u.id === selectedUserId);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1.5">
          <UserCheck className="h-3.5 w-3.5" />
          {currentAssigneeId ? "Reasignar" : "Asignar"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end">
        <p className="px-3 pt-3 pb-2 text-xs font-medium text-muted-foreground">
          Seleccionar usuario de bodega
        </p>
        {loadingUsers ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : !bodegaUsers?.length ? (
          <p className="py-2 text-center text-xs text-muted-foreground">
            No hay usuarios de bodega activos
          </p>
        ) : (
          <div className="space-y-2">
            <Command>
              <CommandInput placeholder="Buscar usuario..." className="h-8" />
              <CommandList className="max-h-[200px]">
                <CommandEmpty>No se encontró usuario</CommandEmpty>
                <CommandGroup>
                  {bodegaUsers.map((u) => (
                    <CommandItem
                      key={u.id}
                      value={`${u.name} ${u.lastname}`}
                      onSelect={() => setSelectedUserId(u.id)}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedUserId === u.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {u.name} {u.lastname}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
            <div className="px-3 pb-3">
              {selectedUser && (
                <p className="mb-2 text-xs text-muted-foreground">
                  Seleccionado: {selectedUser.name} {selectedUser.lastname}
                </p>
              )}
              <Button
                size="sm"
                className="w-full"
                disabled={!selectedUserId || assignMutation.isPending}
                onClick={() => assignMutation.mutate()}
              >
                {assignMutation.isPending ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : null}
                Confirmar asignación
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface BulkAssignPopoverProps {
  taskIds: string[];
  onAssigned: () => void;
  onClearSelection: () => void;
}

function BulkAssignPopover({
  taskIds,
  onAssigned,
  onClearSelection,
}: BulkAssignPopoverProps) {
  const [open, setOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: bodegaUsers, isLoading: loadingUsers } = useQuery({
    queryKey: ["bodega-users"],
    queryFn: () => pickingService.getAssignableBodegaUsers(),
    staleTime: 60_000,
  });

  const assignMutation = useMutation({
    mutationFn: () => pickingService.assignPickTasksBulk(taskIds, selectedUserId),
    onSuccess: (result) => {
      if (result.errorCount === 0) {
        toast({ title: `${result.successCount} tareas asignadas correctamente` });
      } else {
        toast({
          title: "Asignacion parcial",
          description: `${result.successCount} exitosas, ${result.errorCount} fallaron`,
          variant: "destructive",
        });
      }
      queryClient.invalidateQueries({ queryKey: ["picking-tasks-assignment"] });
      setOpen(false);
      setSelectedUserId("");
      onClearSelection();
      onAssigned();
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const selectedUser = bodegaUsers?.find((u) => u.id === selectedUserId);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" className="gap-1.5 bg-blue-600 hover:bg-blue-700">
          <UserCheck className="h-3.5 w-3.5" />
          Asignar ({taskIds.length})
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end">
        <p className="px-3 pt-3 pb-2 text-xs font-medium text-muted-foreground">
          Asignar {taskIds.length} tareas a:
        </p>
        {loadingUsers ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : !bodegaUsers?.length ? (
          <p className="py-2 text-center text-xs text-muted-foreground">
            No hay usuarios de bodega activos
          </p>
        ) : (
          <div className="space-y-2">
            <Command>
              <CommandInput placeholder="Buscar usuario..." className="h-8" />
              <CommandList className="max-h-[200px]">
                <CommandEmpty>No se encontró usuario</CommandEmpty>
                <CommandGroup>
                  {bodegaUsers.map((u) => (
                    <CommandItem
                      key={u.id}
                      value={`${u.name} ${u.lastname}`}
                      onSelect={() => setSelectedUserId(u.id)}
                      className="cursor-pointer"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          selectedUserId === u.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {u.name} {u.lastname}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
            <div className="px-3 pb-3">
              {selectedUser && (
                <p className="mb-2 text-xs text-muted-foreground">
                  Seleccionado: {selectedUser.name} {selectedUser.lastname}
                </p>
              )}
              <Button
                size="sm"
                className="w-full"
                disabled={!selectedUserId || assignMutation.isPending}
                onClick={() => assignMutation.mutate()}
              >
                {assignMutation.isPending ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : null}
                Confirmar asignación
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

const PAGE_SIZE = 10;

export function PickingTaskAssignmentPanel() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [comunaFilter, setComunaFilter] = useState<string>("all");

  const {
    selectedTaskIds,
    handleSelectTask,
    handleSelectAll,
    clearSelection,
    isAllSelected,
    selectedCount,
  } = usePickTaskSelection();

  const {
    data: tasks,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["picking-tasks-assignment"],
    queryFn: () => pickingService.getPickTasksForAssignment(),
    refetchInterval: 30_000,
  });

  const uniqueComunas = useMemo(() => {
    if (!tasks) return [];
    const comunas = tasks
      .map((t) => {
        const sr = t.sales_request as { delivery_comuna?: string } | undefined;
        return sr?.delivery_comuna;
      })
      .filter((c): c is string => Boolean(c));
    return [...new Set(comunas)].sort();
  }, [tasks]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertCircle className="mx-auto mb-2 h-8 w-8 text-red-500" />
          <p className="text-red-600">Error al cargar tareas</p>
        </CardContent>
      </Card>
    );
  }

  if (!tasks?.length) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
          <p className="text-muted-foreground">
            No hay tareas de picking pendientes
          </p>
        </CardContent>
      </Card>
    );
  }

  const filteredTasks = tasks.filter((task) => {
    const sr = task.sales_request as {
      client?: { name: string };
      delivery_comuna?: string;
    } | undefined;

    // Search filter
    if (search.trim()) {
      const clientName = sr?.client?.name?.toLowerCase() ?? "";
      if (!clientName.includes(search.toLowerCase().trim())) {
        return false;
      }
    }

    // Comuna filter (partial match)
    if (comunaFilter !== "all") {
      const taskComuna = sr?.delivery_comuna?.toLowerCase() ?? "";
      if (!taskComuna.includes(comunaFilter.toLowerCase())) {
        return false;
      }
    }

    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filteredTasks.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedTasks = filteredTasks.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  return (
    <div className="space-y-3">
      {/* Filter row */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por cliente..."
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="relative w-full sm:w-48">
          <MapPin className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Filtrar por comuna..."
            value={comunaFilter === "all" ? "" : comunaFilter}
            onChange={(e) => setComunaFilter(e.target.value || "all")}
            className="pl-9"
          />
          {comunaFilter !== "all" && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2"
              onClick={() => setComunaFilter("all")}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>
      </div>

      {/* Bulk action bar (when items selected) */}
      {selectedCount > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-blue-900">
              {selectedCount} tarea{selectedCount > 1 ? "s" : ""} seleccionada
              {selectedCount > 1 ? "s" : ""}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={clearSelection}
              className="gap-1"
            >
              <X className="h-4 w-4" />
              Limpiar
            </Button>
          </div>
          <BulkAssignPopover
            taskIds={Array.from(selectedTaskIds)}
            onAssigned={refetch}
            onClearSelection={clearSelection}
          />
        </div>
      )}

      {/* Select all checkbox header */}
      {filteredTasks.length > 0 && (
        <div className="flex items-center gap-2 border-b pb-2">
          <Checkbox
            checked={isAllSelected(filteredTasks)}
            onCheckedChange={() => handleSelectAll(filteredTasks)}
          />
          <span className="text-xs text-muted-foreground">Seleccionar todo</span>
        </div>
      )}

      {filteredTasks.length === 0 && (search || comunaFilter !== "all") ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Sin resultados{search ? ` para "${search}"` : ""}{comunaFilter !== "all" ? ` en ${comunaFilter}` : ""}
        </p>
      ) : (
        <>
          {pagedTasks.map((task) => {
            const sr = task.sales_request as
              | {
                  id: string;
                  client?: { name: string };
                  delivery_date?: string;
                  price_with_iva?: number;
                  delivery_street?: string;
                  delivery_number?: string;
                  delivery_complement?: string;
                  delivery_comuna?: string;
                  delivery_city?: string;
                }
              | undefined;
            const assignee = (
              task as unknown as {
                assignee?: { name: string; lastname: string };
              }
            ).assignee;
            const taskItems = (
              task as unknown as {
                items?: { id: string; requested_quantity: number; product?: { short_name: string } }[];
              }
            ).items ?? [];
            const totalBoxes = taskItems.reduce(
              (sum, i) => sum + (i.requested_quantity || 0),
              0
            );

            return (
              <TaskCard
                key={task.id}
                task={task}
                sr={sr}
                assignee={assignee}
                taskItems={taskItems}
                totalBoxes={totalBoxes}
                onAssigned={refetch}
                isSelected={selectedTaskIds.has(task.id)}
                onToggleSelect={() => handleSelectTask(task.id)}
              />
            );
          })}

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-muted-foreground">
                {filteredTasks.length} tareas · página {safePage} de {totalPages}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-7 w-7"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

interface TaskCardProps {
  task: { id: string; sales_request_id: string; assigned_to?: string; status: string };
  sr?: {
    id: string;
    client?: { name: string };
    delivery_date?: string;
    price_with_iva?: number;
    delivery_street?: string;
    delivery_number?: string;
    delivery_complement?: string;
    delivery_comuna?: string;
    delivery_city?: string;
  };
  assignee?: { name: string; lastname: string };
  taskItems: { id: string; requested_quantity: number; product?: { short_name: string } }[];
  totalBoxes: number;
  onAssigned: () => void;
  isSelected: boolean;
  onToggleSelect: () => void;
}

function TaskCard({
  task,
  sr,
  assignee,
  taskItems,
  totalBoxes,
  onAssigned,
  isSelected,
  onToggleSelect,
}: TaskCardProps) {
  const [open, setOpen] = useState(false);
  const isInProgress = task.status === "in_progress";

  return (
    <Card className={`overflow-hidden ${isSelected ? "ring-2 ring-blue-500" : ""}`}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            {/* Checkbox for selection */}
            <Checkbox
              checked={isSelected}
              onCheckedChange={onToggleSelect}
              disabled={isInProgress}
              className="mt-0.5"
            />
            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-sm font-medium">
                  {sr?.client?.name ?? "Cliente desconocido"}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                {/* Comuna display */}
                {sr?.delivery_comuna && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {sr.delivery_comuna}
                  </span>
                )}
                {sr?.delivery_date && (
                  <span>
                    Entrega:{" "}
                    {dayjs(sr.delivery_date).format("DD/MM/YYYY")}
                  </span>
                )}
                {assignee ? (
                  <span className="flex items-center gap-1 text-blue-600">
                    <User className="h-3 w-3" />
                    {assignee.name} {assignee.lastname}
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-amber-600">
                    <Package className="h-3 w-3" />
                    Sin asignar
                  </span>
                )}
              </div>
            </div>
          </div>

          <AssignPopover
            taskId={task.id}
            currentAssigneeId={task.assigned_to}
            onAssigned={onAssigned}
          />
        </div>

        {/* Collapsible items list */}
        {taskItems.length > 0 && (
          <Collapsible open={open} onOpenChange={setOpen}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 h-7 w-full justify-start gap-1.5 px-1 text-xs text-muted-foreground hover:text-foreground"
              >
                {open ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
                {taskItems.length} producto{taskItems.length !== 1 ? "s" : ""} · {totalBoxes} cajas
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="mt-1.5 space-y-2 rounded-md border bg-muted/30 p-2">
                {/* Delivery Address */}
                {(sr?.delivery_street || sr?.delivery_comuna) && (
                  <div className="flex items-start gap-2 border-b pb-2 text-xs">
                    <Home className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-foreground">Dirección de entrega</p>
                      <p className="text-muted-foreground">
                        {[
                          sr.delivery_street,
                          sr.delivery_number,
                          sr.delivery_complement,
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      </p>
                      <p className="text-muted-foreground">
                        {[sr.delivery_comuna, sr.delivery_city]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    </div>
                  </div>
                )}
                {/* Items list */}
                <div className="space-y-1">
                  <p className="text-xs font-medium text-foreground">Productos</p>
                  {taskItems.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between gap-2 text-xs"
                    >
                      <span className="min-w-0 flex-1 truncate text-muted-foreground">
                        {item.product?.short_name ?? "Producto"}
                      </span>
                      <span className="shrink-0 font-medium text-muted-foreground">
                        {item.requested_quantity} cajas
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}
